import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Coins, Copy, Check, Building2, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useI18n } from "@/i18n";
import api, { apiError } from "@/api";

const T = {
  de: {
    title: "Credits", leads: "Leads", perLead: "1 Credit pro Kontaktaufnahme",
    buyTitle: "Per Überweisung bezahlen", package: "Paket", amount: "Betrag",
    reference: "Verwendungszweck", refHint: "Gib diese Referenz zwingend bei der Überweisung an – nur so können wir deine Zahlung zuordnen.",
    holder: "Kontoinhaber", bank: "Bank", rib: "RIB", iban: "IBAN", swift: "SWIFT/BIC",
    instructions: "Überweise den Betrag und gib die Referenz als Verwendungszweck an. Sobald die Zahlung eingegangen ist, schalten wir deine Credits frei (i. d. R. innerhalb von 24 Std.).",
    copy: "Kopieren", copied: "Kopiert!", close: "Schließen",
    myOrders: "Deine Bestellungen", pending: "Ausstehend", confirmed: "Bestätigt", cancelled: "Storniert",
    warn0: 'Keine Leads mehr – jetzt aufladen, sonst kannst du dich nicht mehr bewerben.', warn1: 'Nur noch 1 Lead! Jetzt aufladen, damit du keinen Auftrag verpasst.', warn2: 'Nur noch 2 Leads – bald aufladen.',
    memberNo: "Deine Mitgliedsnummer", orderHint: "Tippe auf eine Bestellung, um die Bankverbindung anzuzeigen.",
  },
  en: {
    title: "Credits", leads: "leads", perLead: "1 credit per contact",
    buyTitle: "Pay by bank transfer", package: "Package", amount: "Amount",
    reference: "Payment reference", refHint: "You must add this reference to your transfer – it's how we match your payment.",
    holder: "Account holder", bank: "Bank", rib: "RIB", iban: "IBAN", swift: "SWIFT/BIC",
    instructions: "Transfer the amount and add the reference as the payment reference. Once we receive it, we'll release your credits (usually within 24h).",
    copy: "Copy", copied: "Copied!", close: "Close",
    myOrders: "Your orders", pending: "Pending", confirmed: "Confirmed", cancelled: "Cancelled",
    warn0: 'No leads left – top up now, otherwise you cannot apply any more.', warn1: 'Only 1 lead left! Top up now so you do not miss a job.', warn2: 'Only 2 leads left – top up soon.',
    memberNo: "Your member number", orderHint: "Tap an order to see the bank details.",
  },
  fr: {
    title: "Crédits", leads: "leads", perLead: "1 crédit par prise de contact",
    buyTitle: "Payer par virement", package: "Pack", amount: "Montant",
    reference: "Référence de paiement", refHint: "Indiquez impérativement cette référence lors du virement – c'est ainsi que nous identifions votre paiement.",
    holder: "Titulaire du compte", bank: "Banque", rib: "RIB", iban: "IBAN", swift: "SWIFT/BIC",
    instructions: "Effectuez le virement en indiquant la référence en objet. Dès réception, nous créditons votre compte (généralement sous 24h).",
    copy: "Copier", copied: "Copié !", close: "Fermer",
    myOrders: "Vos commandes", pending: "En attente", confirmed: "Confirmé", cancelled: "Annulé",
    warn0: 'Plus de leads – rechargez maintenant, sinon vous ne pouvez plus postuler.', warn1: "Plus qu'1 lead ! Rechargez maintenant pour ne manquer aucune mission.", warn2: 'Plus que 2 leads – rechargez bientôt.',
    memberNo: "Votre numéro de membre", orderHint: "Touchez une commande pour afficher les coordonnées bancaires.",
  },
  ar: {
    title: "الرصيد", leads: "طلبات", perLead: "نقطة واحدة لكل تواصل",
    buyTitle: "الدفع عبر التحويل البنكي", package: "الباقة", amount: "المبلغ",
    reference: "مرجع الدفع", refHint: "يجب إضافة هذا المرجع عند التحويل – به نتعرف على دفعتك.",
    holder: "صاحب الحساب", bank: "البنك", rib: "RIB", iban: "IBAN", swift: "SWIFT/BIC",
    instructions: "حوّل المبلغ مع إضافة المرجع في خانة السبب. بمجرد استلام الدفعة نضيف رصيدك (عادة خلال 24 ساعة).",
    copy: "نسخ", copied: "تم النسخ!", close: "إغلاق",
    myOrders: "طلباتك", pending: "قيد الانتظار", confirmed: "مؤكد", cancelled: "ملغى",
    warn0: 'لا توجد طلبات متبقية – اشحن الآن وإلا لن تستطيع التقديم.', warn1: 'بقي طلب واحد فقط! اشحن الآن حتى لا تفوتك أي فرصة.', warn2: 'بقي طلبان فقط – اشحن قريباً.',
    memberNo: "رقم عضويتك", orderHint: "اضغط على الطلب لعرض بيانات الحساب البنكي.",
  },
};

const statusStyle = {
  pending: "bg-[#E69625]/15 text-[#9A6B12]",
  confirmed: "bg-[#5C6646]/15 text-[#3F4A2E]",
  cancelled: "bg-[#8C7A70]/15 text-[#6B5A50]",
};

async function copyText(value) {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = value; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.focus(); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
}

function Row({ label, value, id }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    await copyText(value);
    setDone(true);
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 border-b border-[#F0E7DE] last:border-0">
      <div className="min-w-0">
        <div className="text-xs text-[#8C7A70]">{label}</div>
        <div className="font-semibold text-[#2D231F] truncate" dir="ltr">{value}</div>
      </div>
      <button onClick={copy} className="shrink-0 w-8 h-8 rounded-lg bg-[#F2E8DF] hover:bg-[#EAD9CB] flex items-center justify-center transition-colors" data-testid={`copy-${id}`}>
        {done ? <Check className="w-4 h-4 text-[#5C6646]" /> : <Copy className="w-4 h-4 text-[#8C7A70]" />}
      </button>
    </div>
  );
}

export function CreditsPanel() {
  const { lang, money } = useI18n();
  const c = T[lang] || T.de;
  const [balance, setBalance] = useState(null);
  const [packages, setPackages] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(null);
  const [dialog, setDialog] = useState(null); // { order, bank }
  const [bank, setBank] = useState(null);

  const loadOrders = () => api.get("/credits/my-orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/credits/me").then((r) => setBalance(r.data.credits)).catch(() => {});
    api.get("/credits/packages").then((r) => setPackages(r.data)).catch(() => {});
    api.get("/credits/bank-details").then((r) => setBank(r.data.bank)).catch(() => {});
    loadOrders();
  }, []);

  const buy = async (lookup_key) => {
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/credits/manual-order", { lookup_key });
      setDialog(data);
      loadOrders();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="rounded-2xl border border-[#EBDDD3] bg-white p-6" data-testid="credits-panel">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-display text-xl font-bold text-[#2D231F]">{c.title}</h2>
        <span className="w-10 h-10 rounded-xl bg-[#E69625]/15 flex items-center justify-center">
          <Coins className="w-5 h-5 text-[#E69625]" />
        </span>
      </div>
      <div className="flex items-baseline gap-2 mb-1">
        <span className={`font-display text-4xl font-black ${balance === null ? "text-[#C65338]" : balance <= 1 ? "text-[#B0413E]" : balance === 2 ? "text-[#C77F10]" : "text-[#C65338]"}`} data-testid="credits-balance">{balance ?? "…"}</span>
        <span className="text-sm text-[#8C7A70]">{c.leads}</span>
      </div>
      <p className="text-xs text-[#8C7A70] mb-4">{c.perLead}</p>
      {balance !== null && balance <= 2 && (
        <div data-testid="credits-warning" role="alert"
          className={`mb-5 rounded-xl px-4 py-3 text-sm font-bold flex items-start gap-2.5 border-2 ${balance <= 1 ? "bg-[#B0413E]/10 border-[#B0413E] text-[#8E2F2C]" : "bg-[#E69625]/15 border-[#E69625] text-[#8A5A10]"}`}>
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <span>{balance === 0 ? c.warn0 : balance === 1 ? c.warn1 : c.warn2}</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {packages.map((p) => (
          <button key={p.lookup_key} data-testid={`buy-${p.lookup_key}`} disabled={loading}
            onClick={() => buy(p.lookup_key)}
            className="group rounded-xl border-2 border-[#EBDDD3] hover:border-[#C65338] p-4 text-left transition-colors disabled:opacity-60">
            <div className="font-display text-2xl font-black text-[#2D231F]">{p.credits}</div>
            <div className="text-xs text-[#8C7A70] mb-2">{c.leads}</div>
            <div className="font-semibold text-[#C65338]">{money(p.price)}</div>
          </button>
        ))}
      </div>

      {orders.length > 0 && (
        <div className="mt-6" data-testid="my-orders">
          <h3 className="text-sm font-bold text-[#2D231F] mb-1">{c.myOrders}</h3>
          <p className="text-xs text-[#8C7A70] mb-2">{c.orderHint}</p>
          <div className="space-y-2">
            {orders.map((o) => (
              <button type="button" key={o._id} onClick={() => bank && setDialog({ order: o, bank })} disabled={!bank}
                className="w-full text-left flex items-center justify-between gap-2 rounded-lg bg-[#FAF5F0] hover:bg-[#F2E8DF] px-3 py-2 transition-colors cursor-pointer" data-testid={`order-${o._id}`}>
                <div className="min-w-0">
                  <div className="font-mono text-xs text-[#2D231F] truncate">{o.reference}</div>
                  <div className="text-xs text-[#8C7A70]">{o.credits} {c.leads} · {money(o.amount)}</div>
                </div>
                <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${statusStyle[o.status] || ""}`}>{c[o.status] || o.status}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-w-md rtl:[&>button]:left-4 rtl:[&>button]:right-auto" data-testid="manual-payment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-xl">
              <Landmark className="w-5 h-5 text-[#C65338]" /> {c.buyTitle}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#8C7A70]">{c.instructions}</DialogDescription>
          </DialogHeader>
          {dialog && (
            <div>
              <div className="flex items-center justify-between rounded-xl bg-[#C65338]/10 px-4 py-3 mb-4">
                <div>
                  <div className="text-xs text-[#8C7A70]">{c.package}</div>
                  <div className="font-bold text-[#2D231F]">{dialog.order.credits} {c.leads}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-[#8C7A70]">{c.amount}</div>
                  <div className="font-display text-xl font-black text-[#C65338]">{money(dialog.order.amount)}</div>
                </div>
              </div>

              {/* Reference — the key field */}
              <div className="rounded-xl border-2 border-[#C65338]/40 bg-[#FFF9F5] p-3 mb-4" data-testid="payment-reference">
                <div className="text-xs font-bold uppercase tracking-wider text-[#C65338] mb-1">{c.reference}</div>
                <RefField value={dialog.order.reference} copy={c.copy} copied={c.copied} />
                <p className="text-xs text-[#8C7A70] mt-2 leading-relaxed">{c.refHint}</p>
              </div>

              {/* Bank details */}
              <div className="rounded-xl border border-[#EBDDD3] px-4 py-1 mb-4">
                <Row label={c.holder} value={dialog.bank.account_holder} id="holder" />
                {dialog.bank.bank && <Row label={c.bank} value={dialog.bank.bank} id="bank" />}
                <Row label={c.rib} value={dialog.bank.rib} id="rib" />
                {dialog.bank.iban && <Row label={c.iban} value={dialog.bank.iban} id="iban" />}
                {dialog.bank.swift && <Row label={c.swift} value={dialog.bank.swift} id="swift" />}
              </div>

              <p className="text-sm text-[#5A4B45] leading-relaxed mb-4">{c.instructions}</p>
              <Button onClick={() => setDialog(null)} className="w-full rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white h-11" data-testid="close-payment-dialog">{c.close}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RefField({ value, copy, copied }) {
  const [done, setDone] = useState(false);
  const doCopy = async () => {
    await copyText(value);
    setDone(true);
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <div className="flex items-center gap-2">
      <span className="flex-1 font-mono text-lg font-black text-[#2D231F] tracking-wide" dir="ltr" data-testid="reference-value">{value}</span>
      <Button size="sm" onClick={doCopy} className="rounded-lg bg-[#C65338] hover:bg-[#A93F25] text-white gap-1.5" data-testid="copy-reference">
        {done ? <><Check className="w-4 h-4" /> {copied}</> : <><Copy className="w-4 h-4" /> {copy}</>}
      </Button>
    </div>
  );
}
